import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import type { AppAbility } from '@ast-data/shared';
import type { NavItem } from '../../components/NavList';
import {
    allowedFieldCategories,
    FIELD_TIME_ENTRY_KEYS,
    type FieldCategory
} from './fieldCategories';
import { fieldPickerPath } from './useRestoreFieldProject';
import type { FieldRoute } from './useFieldRoute';

/**
 * The field drawer's menu: the project's permitted recording categories, then
 * Zeiterfassung on its own, then the project switcher. Empty on the picker page,
 * where there is no project to offer sections for. The Befund editors are
 * fullscreen dialogs with their own close button, so the menu no longer needs to
 * nest a Befund's Planums/Profils to escape them.
 */
export function useFieldNavGroups(
    route: FieldRoute,
    ability: AppAbility
): NavItem[][] {
    const { projectId } = route;

    if (!projectId) {
        return [];
    }

    function toNavItem(category: FieldCategory): NavItem {
        const to = `/field/${projectId}/${category.key}`;
        return {
            to,
            labelId: category.labelId,
            icon: category.icon,
            // A record dialog deepens the path (…/diaries/new); keep the
            // owning category highlighted for any of its sub-routes.
            isActive: (path: string) =>
                path === to ||
                path === `/field/${projectId}` ||
                path.startsWith(`${to}/`)
        };
    }

    const categories = allowedFieldCategories(ability, projectId);
    // The two Zeiterfassungen (Mitarbeiter, Geräte) form a section of their own
    // below the recording categories, so NavList rules a divider above them —
    // they document the workday, not the excavation. NavList drops empty
    // sections, so a user who may not record time gets no stray divider.
    const isTimeEntry = (category: FieldCategory) =>
        FIELD_TIME_ENTRY_KEYS.includes(category.key);
    return [
        categories.filter((category) => !isTimeEntry(category)).map(toNavItem),
        categories.filter(isTimeEntry).map(toNavItem),
        [
            {
                /**
                 * The same picker path the project header uses: marked as a
                 * deliberate switch (so the last-project restore doesn't send
                 * the user back), carrying where they are switching from.
                 */
                to: fieldPickerPath(route),
                labelId: 'field.nav.switchProject',
                icon: <UnfoldMoreIcon />,
                isActive: () => false
            }
        ]
    ];
}
